import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Plus, Edit2, Trash2, Search, Star } from 'lucide-react'
import { useInventoryStore } from '../store/inventoryStore'

export default function SupplierManagementPage() {
  const navigate = useNavigate()
  const suppliers = useInventoryStore((state) => state.suppliers)
  const addSupplier = useInventoryStore((state) => state.addSupplier)
  const updateSupplier = useInventoryStore((state) => state.updateSupplier)
  const deleteSupplier = useInventoryStore((state) => state.deleteSupplier)

  const [searchTerm, setSearchTerm] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedSupplier, setSelectedSupplier] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    phone: '',
    rating: 85,
    onTime: 90,
    quality: 90,
    reliability: 90
  })

  const filteredSuppliers = useMemo(() => {
    return suppliers.filter(supplier =>
      supplier.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [suppliers, searchTerm])

  const handleAddSupplier = () => {
    if (formData.name && formData.contact) {
      addSupplier(formData)
      setFormData({
        name: '',
        contact: '',
        email: '',
        phone: '',
        rating: 85,
        onTime: 90,
        quality: 90,
        reliability: 90
      })
      setShowAddModal(false)
    }
  }

  const handleEditSupplier = (supplier) => {
    setSelectedSupplier(supplier.id)
    setFormData(supplier)
    setShowAddModal(true)
  }

  const handleUpdateSupplier = () => {
    if (selectedSupplier && formData.name && formData.contact) {
      updateSupplier(selectedSupplier, formData)
      setFormData({
        name: '',
        contact: '',
        email: '',
        phone: '',
        rating: 85,
        onTime: 90,
        quality: 90,
        reliability: 90
      })
      setSelectedSupplier(null)
      setShowAddModal(false)
    }
  }

  const handleDeleteSupplier = (id) => {
    if (confirm('Delete this supplier?')) {
      deleteSupplier(id)
    }
  }

  const closeModal = () => {
    setShowAddModal(false)
    setSelectedSupplier(null)
    setFormData({
      name: '',
      contact: '',
      email: '',
      phone: '',
      rating: 85,
      onTime: 90,
      quality: 90,
      reliability: 90
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button onClick={() => navigate(-1)} className="text-blue-400 hover:text-blue-300">←</button>
            <h1 className="text-4xl font-bold text-white flex items-center space-x-3">
              <Users className="w-8 h-8 text-teal-400" />
              <span>Supplier Management</span>
            </h1>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-all"
          >
            <Plus className="w-5 h-5" />
            <span>Add Supplier</span>
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-3 w-5 h-5 text-blue-400" />
          <input
            type="text"
            placeholder="Search suppliers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-slate-800 border border-blue-500/20 rounded-lg pl-10 pr-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
          />
        </div>

        {/* Suppliers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSuppliers.map((supplier) => (
            <div key={supplier.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-white">{supplier.name}</h3>
                  <p className="text-blue-300 text-sm">{supplier.contact}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditSupplier(supplier)}
                    className="text-blue-400 hover:text-blue-300 p-2 hover:bg-blue-500/10 rounded transition-all"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteSupplier(supplier.id)}
                    className="text-red-400 hover:text-red-300 p-2 hover:bg-red-500/10 rounded transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="text-sm">
                  <p className="text-blue-300 mb-1">Email</p>
                  <p className="text-white text-xs">{supplier.email}</p>
                </div>
                <div className="text-sm">
                  <p className="text-blue-300 mb-1">Phone</p>
                  <p className="text-white">{supplier.phone}</p>
                </div>

                {/* Rating */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-blue-300 text-xs font-semibold">Overall Rating</span>
                    <div className="flex items-center space-x-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span className="text-yellow-400 font-bold">{supplier.rating}%</span>
                    </div>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-yellow-500" style={{width: `${supplier.rating}%`}}></div>
                  </div>
                </div>

                {/* Performance Metrics */}
                <div className="grid grid-cols-3 gap-2 pt-2">
                  <div className="bg-slate-700/50 rounded p-2 text-center">
                    <p className="text-blue-300 text-xs">On-Time</p>
                    <p className="text-green-400 font-bold text-sm">{supplier.onTime}%</p>
                  </div>
                  <div className="bg-slate-700/50 rounded p-2 text-center">
                    <p className="text-blue-300 text-xs">Quality</p>
                    <p className="text-green-400 font-bold text-sm">{supplier.quality}%</p>
                  </div>
                  <div className="bg-slate-700/50 rounded p-2 text-center">
                    <p className="text-blue-300 text-xs">Reliability</p>
                    <p className="text-green-400 font-bold text-sm">{supplier.reliability}%</p>
                  </div>
                </div>

                <div className="pt-2">
                  <span className="inline-block text-xs font-bold px-2 py-1 rounded bg-green-500/20 text-green-300">
                    {supplier.status ? supplier.status.toUpperCase() : 'ACTIVE'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                {selectedSupplier ? 'Edit Supplier' : 'Add New Supplier'}
              </h2>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Supplier Name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="text"
                  placeholder="Contact Person"
                  value={formData.contact}
                  onChange={(e) => setFormData({...formData, contact: e.target.value})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="number"
                  placeholder="Rating %"
                  min="0" max="100"
                  value={formData.rating}
                  onChange={(e) => setFormData({...formData, rating: parseInt(e.target.value)})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="number"
                  placeholder="On-Time %"
                  min="0" max="100"
                  value={formData.onTime}
                  onChange={(e) => setFormData({...formData, onTime: parseInt(e.target.value)})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={closeModal}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={selectedSupplier ? handleUpdateSupplier : handleAddSupplier}
                  className="flex-1 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-all"
                >
                  {selectedSupplier ? 'Update' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
